import clsx from 'clsx';
import PropTypes, { string } from 'prop-types';
import React from 'react';

import BellNarrow from './images/bell-narrow.inline.svg';
import BellWide from './images/bell-wide.inline.svg';
import Book from './images/book.inline.svg';
import Click from './images/click.inline.svg';
import Connection from './images/connection.inline.svg';
import Document from './images/document.inline.svg';
import ExportIcon from './images/export.inline.svg';
import GraphArea from './images/graph-area.inline.svg';
import GraphBar from './images/graph-bar.inline.svg';
import Key from './images/key.inline.svg';
import Message from './images/message.inline.svg';
import Presentation from './images/presentation.inline.svg';
import Repeat from './images/repeat.inline.svg';
import Target from './images/target.inline.svg';

const icons = {
  bellNarrow: BellNarrow,
  bellWide: BellWide,
  book: Book,
  click: Click,
  connection: Connection,
  document: Document,
  export: ExportIcon,
  graphArea: GraphArea,
  graphBar: GraphBar,
  key: Key,
  message: Message,
  presentation: Presentation,
  repeat: Repeat,
  target: Target,
};

const itemsTheme = [
  {
    bg: 'bg-[radial-gradient(circle,rgb(193,246,224)_0%,rgba(255,255,255,0)_100%)]',
    shadow: 'shadow-[0_1.6px_6.4px_0_rgba(76,178,136,0.16),0_4.8px_16px_0_rgba(219,240,231,0.60)]',
    iconColor: 'text-secondary-1',
  },
  {
    bg: 'bg-[radial-gradient(circle,rgb(193,201,246)_0%,rgba(255,255,255,0)_100%)]',
    imageShadow: 'shadow-[0_2px_8px_0_rgba(92,112,214,0.20),0_6px_20px_0_rgba(230,233,248,0.70)]',
    iconColor: 'text-secondary-2',
  },
  {
    bg: 'bg-[radial-gradient(circle,rgb(247,212,212)_0%,rgba(255,255,255,0)_100%)]',
    imageShadow: 'shadow-[0_2px_8px_0_rgba(191,64,64,0.12),0_6px_20px_0_rgba(248,230,230,0.60)]',
    iconColor: 'text-new-primary-1',
  },
];

const CategoryInfo = ({ eventsDescription, info, events }) => (
  <>
    {info && (
      <ul className="grid gap-8 grid-cols-3 lg:grid-cols-2 md:grid-cols-1">
        {info.map(({ title, iconName, text }, index) => {
          const Icon = icons[iconName];

          return (
            <li
              className="relative grid grid-cols-[48px_1fr] grid-rows-[min-content_1fr] gap-x-3 gap-y-3.5 items-center min-h-44 py-4 px-6 bg-new-gray-2 border border-[#eaedf6] rounded-2xl overflow-hidden md:min-h-0 md:px-3.5"
              key={title}
            >
              <div
                className={clsx(
                  'absolute -top-32 -left-32 w-96 h-96 opacity-15 blur',
                  itemsTheme[index].bg
                )}
              />
              <div
                className={clsx(
                  'relative flex justify-center items-center w-12 h-12 bg-white rounded-full',
                  itemsTheme[index].shadow,
                  itemsTheme[index].iconColor
                )}
              >
                <Icon className="size-5" />
              </div>
              <h3 className="relative font-sans2 text-lg leading-denser -tracking-[0.96px] text-new-black">
                {title}
              </h3>
              <p className="relative col-span-2 self-start font-light text-base leading-normal text-new-gray-1">
                {text}
              </p>
            </li>
          );
        })}
      </ul>
    )}
    {events && (
      <div className={clsx('p-6 border border-[#d6dbf5] rounded-2xl', info && 'mt-8')}>
        <h3 className="mb-2.5 font-medium leading-snug tracking-tight opacity-70">
          {eventsDescription}
        </h3>
        <ul className="flex flex-wrap items-center">
          {events.map((event, index) => (
            <li
              className={clsx(
                'flex items-center text-base leading-snug text-new-black/70 whitespace-nowrap',
                index !== events.length - 1 &&
                  'after:relative after:-top-px after:size-1.5 after:mx-3 after:rounded-full after:bg-[#c8cad0] sm:after:size-1 sm:after:mx-2'
              )}
              key={event}
            >
              {event}
            </li>
          ))}
        </ul>
      </div>
    )}
  </>
);
CategoryInfo.propTypes = {
  eventsDescription: PropTypes.string.isRequired,
  info: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      iconName: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
  events: PropTypes.arrayOf(string),
};

CategoryInfo.defaultProps = {
  info: null,
  events: null,
};

export default CategoryInfo;
